/**
 * Shared API helpers + job-polling hook for the Signal Alpha Lab.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";

export const API = process.env.REACT_APP_BACKEND_URL + "/api";

export async function apiGet(path, token) {
  const res = await fetch(`${API}${path}`, {
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed: ${res.status}`);
  }
  return res.json();
}

export async function apiPost(path, token) {
  const res = await fetch(`${API}${path}`, {
    method: "POST",
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!res.ok) {
    const err = await res.json().catch(() => ({}));
    throw new Error(err.detail || `Request failed: ${res.status}`);
  }
  return res.json();
}

/**
 * Manages one signal's cached result + background job lifecycle.
 * statusPath/resultPath allow reuse for the white paper job.
 */
export function useSignalJob(signalKey, token, { onComplete } = {}) {
  const [result, setResult] = useState(null);
  const [job, setJob] = useState(null);
  const [running, setRunning] = useState(false);
  const [loading, setLoading] = useState(true);
  const pollRef = useRef(null);

  const reload = useCallback(async () => {
    try {
      const data = await apiGet(`/stats/signal-alpha/${signalKey}`, token);
      setResult(data.status === "not_computed" ? null : data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  }, [signalKey, token]);

  const pollStatus = useCallback(async () => {
    try {
      const st = await apiGet(`/stats/signal-alpha/${signalKey}/status`, token);
      setJob(st);
      if (st.status === "running") {
        setRunning(true);
        return false;
      }
      setRunning(false);
      return true;
    } catch (e) {
      return true;
    }
  }, [signalKey, token]);

  // On mount: load cached result and resume polling if a job is running
  useEffect(() => {
    if (!token) return;
    reload();
    (async () => {
      const finished = await pollStatus();
      if (!finished) startPolling();
    })();
    return () => clearInterval(pollRef.current);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token, signalKey]);

  const startPolling = useCallback(() => {
    clearInterval(pollRef.current);
    pollRef.current = setInterval(async () => {
      const finished = await pollStatus();
      if (finished) {
        clearInterval(pollRef.current);
        const st = await apiGet(`/stats/signal-alpha/${signalKey}/status`, token).catch(() => null);
        if (st?.status === "completed") {
          toast.success(`${signalKey} analysis complete`);
          await reload();
          onComplete && onComplete();
        } else if (st?.status === "failed") {
          toast.error(`${signalKey} analysis failed: ${st.error || "unknown error"}`);
        }
      }
    }, 3000);
  }, [pollStatus, reload, signalKey, token, onComplete]);

  const run = useCallback(async () => {
    try {
      await apiPost(`/stats/signal-alpha/${signalKey}/run`, token);
      setRunning(true);
      setJob({ status: "running", progress: 0, message: "Starting..." });
      toast.info(`Started ${signalKey} analysis`);
      startPolling();
    } catch (e) {
      toast.error(e.message);
    }
  }, [signalKey, token, startPolling]);

  return { result, job, running, loading, run, reload };
}
